<template>
  <div class="aviso" v-if="!checkAviso && marcadores.length === 0">
    <span>
      Não há atividades com locais cadastrados para este ano!
    </span>
  </div>
  <div ref="mapaEl" class="mapa-leaflet" :class="{ 'is-selecionavel': selecionavel }" :style="{ height: altura }"></div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import L, { icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});


const props = defineProps({
  marcadores: {
    type: Array,
    default: () => null,
  },
  selecionavel: {
    type: Boolean,
    default: false,
  },
  centro: {
    type: Array,
    default: () => [-3.7319, -38.5267], // Fortaleza
  },
  zoom: {
    type: Number,
    default: 13,
  },
  // Zoom usado quando há apenas um marcador para focar
  zoomMarcador: {
    type: Number,
    default: 16,
  },
  // Ajusta a visão automaticamente para enquadrar os marcadores quando eles mudam
  ajustarAosMarcadores: {
    type: Boolean,
    default: true,
  },
  altura: {
    type: String,
    default: '320px',
  },
});

const emit = defineEmits(['selecionar', 'clique-marcador']);

const router = useRouter();
const route = useRoute()

const mapaEl = ref(null);
let mapa = null;
let camadaMarcadores = null;
let resizeObserver = null;

const checkAviso = computed(() => {
  return route.name === 'cadastrar-atividade' || route.name === 'editar-atividade'
})

function coordValida(m) {
  const lat = Number(m?.latitude);
  const lon = Number(m?.longitude);
  return (
    m?.latitude !== null && m?.latitude !== undefined && m?.latitude !== '' &&
    m?.longitude !== null && m?.longitude !== undefined && m?.longitude !== '' &&
    !isNaN(lat) && !isNaN(lon) &&
    lat >= -90 && lat <= 90 &&
    lon >= -180 && lon <= 180
  );
}

function escaparHtml(texto) {
  return String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderizarMarcadores() {
  if (!mapa || !camadaMarcadores) return;
  camadaMarcadores.clearLayers();

  const validos = props.marcadores.filter(coordValida);

  var iconePersonalizado = L.divIcon({
    className: 'custom-map-marker',
    html: `<i class="mdi mdi-map-marker" style="color: var(--roxo-claro); font-size: 38px; line-height: 1; display: flex; justify-content: center; align-items: center;"></i>`,
    iconSize: [38, 38],
    iconAnchor: [19, 36],
    popupAnchor: [0, -36]
  });

  validos.forEach((m) => {
    const marker = L.marker([Number(m.latitude), Number(m.longitude)], { icon: iconePersonalizado });

    if (m.nomeAtividade || m.nomeAcao || m.localLabel || m.local) {
      const conteudo = document.createElement('div');
      conteudo.style.display = 'flex';
      conteudo.style.flexDirection = 'column';
      conteudo.style.gap = '8px';

      const subLocal = m.localLabel || m.subtitulo || m.local || '';
      conteudo.innerHTML = `
        ${m.nomeAtividade ? `<strong>${escaparHtml(m.nomeAtividade)}</strong>` : ''}
        ${m.nomeAcao ? `<div>${escaparHtml(m.nomeAcao)}</div>` : ''}
        ${subLocal ? `<div style="font-size:12px;color:#555;">${escaparHtml(subLocal)}</div>` : ''}
      `;

      if (m.atividadeId) {
        const btn = document.createElement('button');
        btn.textContent = 'Ver Atividade';
        btn.style.backgroundColor = 'var(--roxo-primario)';
        btn.style.color = 'white';
        btn.style.border = 'none';
        btn.style.borderRadius = '50px';
        btn.style.padding = '6px 12px';
        btn.style.cursor = 'pointer';
        btn.style.marginTop = '4px';
        btn.style.fontWeight = '500';
        btn.style.fontSize = '12px';
        btn.style.transition = 'opacity 0.2s';
        btn.onmouseover = () => btn.style.opacity = '0.9';
        btn.onmouseout = () => btn.style.opacity = '1';

        btn.onclick = () => {
          router.push(`/atividades/${m.atividadeId}`);
        };
        conteudo.appendChild(btn);
      }

      marker.bindPopup(conteudo);
    }

    marker.on('click', () => {
      emit('clique-marcador', m);
      mapa.setView([Number(m.latitude), Number(m.longitude)])
    });
    camadaMarcadores.addLayer(marker);
  });

  if (props.ajustarAosMarcadores) enquadrar(validos);
}

function enquadrar(validos) {
  if (!mapa || !validos.length) return;
  if (validos.length === 1) {
    mapa.setView([Number(validos[0].latitude), Number(validos[0].longitude)], props.zoomMarcador);
  } else {
    const bounds = L.latLngBounds(validos.map((m) => [Number(m.latitude), Number(m.longitude)]));
    mapa.fitBounds(bounds, { padding: [30, 30] });
  }
}

function iniciarMapa() {
  if (mapa || !mapaEl.value) return;

  mapa = L.map(mapaEl.value, {
    zoomControl: false,
    maxBounds: [
      [-90, -180],
      [90, 180]
    ],
    maxBoundsViscosity: 1.0
  }).setView(props.centro, props.zoom);
  L.control.zoom({ position: 'bottomright' }).addTo(mapa);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    minZoom: 3,
    bounds: [
      [-90, -180],
      [90, 180]
    ],
    attribution: '&copy; OpenStreetMap contributors',
  }).addTo(mapa);

  camadaMarcadores = L.markerClusterGroup({
    iconCreateFunction: function (cluster) {
      const count = cluster.getChildCount();
      return L.divIcon({
        html: `<div style="background-color: var(--roxo-claro); color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; justify-content: center; align-items: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${count}</div>`,
        className: 'custom-cluster-icon',
        iconSize: L.point(40, 40)
      });
    }
  }).addTo(mapa);

  renderizarMarcadores();

  mapa.on('click', (e) => {
    if (!props.selecionavel) return;
    emit('selecionar', {
      latitude: Number(e.latlng.lat.toFixed(6)),
      longitude: Number(e.latlng.lng.toFixed(6)),
    });
  });

  // Corrige o tamanho do mapa quando o container muda (ex.: aberto com v-show, transições, resize)
  resizeObserver = new ResizeObserver(() => mapa?.invalidateSize());
  resizeObserver.observe(mapaEl.value);
}

watch(() => props.marcadores, renderizarMarcadores, { deep: true });

onMounted(iniciarMapa);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (mapa) {
    mapa.remove();
    mapa = null;
    camadaMarcadores = null;
  }
});

// Métodos expostos para uso via ref no componente pai
defineExpose({
  invalidarTamanho: () => mapa?.invalidateSize(),
  centralizar: (latitude, longitude, zoom = props.zoomMarcador) =>
    mapa?.setView([Number(latitude), Number(longitude)], zoom),
  enquadrarMarcadores: () => enquadrar(props.marcadores.filter(coordValida)),
});
</script>

<style scoped>
.mapa-leaflet {
  width: 100%;
  z-index: 1;
  border-radius: 15px;
  border: 1px solid var(--roxo-medio)
}

.mapa-leaflet.is-selecionavel.leaflet-container {
  cursor: crosshair;
}

.aviso {
  color: darkred;
  font-weight: 700;
  text-align: center;
}
</style>
